import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  formatRedemptionCode,
  formatRedemptionCodeWhileTyping,
  normalizeRedemptionCodeInput,
} from '../../utils/prepaid-redemption-code.util';

/**
 * CWS-1908: "Type the code instead". Opened by the host when a scanned prepaid QR could not
 * be resolved; the attendant types the code from the customer's SMS or email, the host looks
 * the book up and opens the same redemption flow the QR would have opened. Presentational:
 * the host owns busy, error, the reason it opened and what happens on Find.
 *
 * @selector app-prepaid-code-dialog
 * @input isOpen - Shown or not; the field is cleared each time it opens.
 * @input intro - Why it opened (what went wrong with the QR); replaces the default hint.
 * @input busy - A lookup is in flight; Find is disabled and the dialog cannot be dismissed.
 * @input error - The host's refusal, shown under the field.
 * @output find - The complete code, as XXXX-XXXX.
 * @output closed - Dismissed without finding.
 */
@Component({
  selector: 'app-prepaid-code-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './prepaid-code-dialog.component.html',
  styleUrls: ['./prepaid-code-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PrepaidCodeDialogComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() busy = false;
  @Input() error = '';
  @Input() intro = '';

  @Output() find = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  value = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.value = '';
    }
  }

  get canFind(): boolean {
    return !this.busy && normalizeRedemptionCodeInput(this.value) !== null;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = formatRedemptionCodeWhileTyping(target.value);
    target.value = this.value;
  }

  submit(): void {
    const code = normalizeRedemptionCodeInput(this.value);
    if (!code || this.busy) {
      return;
    }
    this.find.emit(formatRedemptionCode(code));
  }

  close(): void {
    if (this.busy) {
      return;
    }
    this.closed.emit();
  }
}
